import React from "react";
import { GreyCard, WarningBox, InfoBox } from "./UI";

export default function PlatformFeatures() {
  return (
    <div className="sz-page-section" id="features">
      <div className="container">
        <h2 className="sz-plain-title mb-1">Platform Features</h2>
        <p>
          After you successfully log in to Selectezi, you'll arrive at your
          User Dashboard, your central hub for managing every aspect of your
          business presence on the platform.
        </p>
        <p className="mb-2">
          Your dashboard is designed to be clean, professional, and
          user-friendly, allowing you to:
        </p>
        <ul className="sz-bullet-list">
          <li>Navigate easily between features</li>
          <li>Update or modify your business information</li>
          <li>Interact seamlessly with other verified businesses</li>
        </ul>
        <p className="mb-4">
          Each user's dashboard is divided into specific modules, each with a
          unique purpose, helping you maintain professionalism, engage
          efficiently, and expand your business network.
        </p>

        <div className="sz-outer-card">
        <h4 className="fw-bold mb-2" style={{ color: "var(--sz-primary)" }}>
          Business Profile (Your Professional Identity)
        </h4>
        <p className="mb-3">
          Your Business Profile acts as your virtual business card on
          Selectezi. It allows others to view who you are and what you do.
        </p>
        <p className="mb-3">
          When creating or editing your profile, you'll need to include the
          following information:
        </p>

        <GreyCard title="Company Name or Personal Business Name">
          <p className="mb-1">
            Enter your official business or brand name. If you're a
            freelancer or service provider, you may use your professional
            name. Your name will appear on your public profile and in search
            results.
          </p>
          <p className="mb-0 fw-semibold" style={{ color: "var(--sz-danger-text)" }}>
            Copied, fake, or misleading names are strictly prohibited.
          </p>
        </GreyCard>
        <GreyCard title="Logo or Profile Image">
          <p className="mb-0">
            Upload your official company logo or professional photo.
            Supported formats: JPG, PNG (under 2MB). Logos enhance branding
            and trustworthiness among users.
          </p>
        </GreyCard>
        <GreyCard title="Business Type / Category">
          <p className="mb-0">
            Select the correct business category. This determines which
            types of businesses you can interact or connect with.
          </p>
        </GreyCard>
        <GreyCard title="Business Details and About Section">
          <p className="mb-1">
            Provide a concise and professional overview of your business.
            Include details such as:
          </p>
          <ul className="mb-1">
            <li>Products or services offered</li>
            <li>Experience and specialization</li>
            <li>Business goals or mission statement</li>
          </ul>
          <p className="mb-0">Use formal, clear language to inspire trust and credibility.</p>
        </GreyCard>
        <GreyCard title="Business Address and Contact Information">
          <p className="mb-0">
            Add your full office/shop address and accurate contact details.
            This ensures verification accuracy and helps potential customers
            or partners contact you easily.
          </p>
        </GreyCard>

        <hr className="sz-divider mb-3" />
        <h5 className="fw-bold mb-2">Key Notes and Guidelines</h5>
        <WarningBox>
          <strong>Misuse or Impersonation: </strong>Any user attempting to
          impersonate another individual or business will face immediate
          suspension. Selectezi enforces a zero-tolerance policy toward fake,
          duplicate, or deceptive profiles.
        </WarningBox>
        <InfoBox>
          <strong>Profile Accuracy: </strong>Keep your business details up to
          date and accurate. Incomplete or incorrect profiles may experience
          reduced visibility within the network.
        </InfoBox>
        </div>

        {/* Misuse Policy */}
        <h2 className="sz-plain-title mb-1">6. Platform Misuse Policy: Rules and Penalties</h2>
        <p className="mb-4">
          To preserve the trust, safety, and integrity of the Selectezi
          community, we strictly enforce a Misuse Prevention Policy. All
          users must comply with these rules to maintain a professional and
          respectful environment.
        </p>

        <div className="sz-outer-card">
        <h5 className="fw-bold mb-3" style={{ color: "var(--sz-primary)" }}>Misuse Actions</h5>
        <div className="row g-3 mb-4">
          {[
            ["Fake Business Info", "Providing false company names, registration numbers, or forged documents."],
            ["Multiple Fake Accounts", "Creating multiple accounts for spamming or data manipulation."],
            ["Irrelevant or Personal Posting", "Sharing non-business or offensive content (e.g., memes, personal opinions, or unrelated images)."],
            ["Harassment or Abusive Communication", "Using Messenger or Timeline to send insults, threats, or harassment."],
            ["Identity Theft", "Using someone else's name, documents, or business details without permission."],
            ["Cross Category Misuse", "Posting in a business category not related to your account type (e.g., a Retailer posing as a Manufacturer)."],
          ].map(([t, d]) => (
            <div className="col-md-6" key={t}>
              <div className="sz-grey-card mb-0">
                <p className="mb-0"><strong>{t}: </strong>{d}</p>
              </div>
            </div>
          ))}
        </div>

        <hr className="sz-divider mb-3" />
        <h5 className="fw-bold mb-2" style={{ color: "var(--sz-primary)" }}>Penalties for Violations</h5>
        <p className="mb-3">
          Selectezi uses a three-step disciplinary procedure to address
          misuse fairly and consistently:
        </p>
        <div className="sz-penalty-step sz-penalty-step-1">
          <strong>Step 1:</strong> Initial Warning to Temporary Restriction:
          Certain features (like posting or messaging) are disabled for 24
          hours.
        </div>
        <div className="sz-penalty-step sz-penalty-step-2">
          <strong>Step 2:</strong> Second Warning to Account Suspension:
          Account is temporarily suspended pending review by the Compliance
          Team.
        </div>
        <div className="sz-penalty-step sz-penalty-step-3">
          <strong>Step 3:</strong> Third Offense to Permanent Ban: Account is
          permanently deleted and all associated data removed.
        </div>
        <p className="fw-semibold mt-2 mb-0" style={{ color: "var(--sz-danger-text)" }}>
          Serious or repeated offenses (such as fraud or impersonation) can
          lead to an instant permanent ban without prior warnings.
        </p>
        </div>

        {/* Best Tips */}
        <h2 className="sz-plain-title mt-5 mb-1">7. Best Experience Tips for Users</h2>
        <p className="mb-4">
          Here are some simple yet powerful tips to help you enjoy the best
          experience on Selectezi, protect your account, and maintain
          professionalism:
        </p>
        <div className="row g-3 mb-5">
          <div className="col-md-6">
            <GreyCard title="Security and Privacy">
              <ul className="mb-0">
                <li>Keep your login information private, never share your password or email.</li>
                <li>Always log out on shared or public devices.</li>
                <li>Use a strong, unique password and update it regularly.</li>
              </ul>
            </GreyCard>
          </div>
          <div className="col-md-6">
            <GreyCard title="Profile Management">
              <ul className="mb-0">
                <li>Keep your business profile updated with the latest contact info, logo, and business category.</li>
                <li>Check your profile completion, verified profiles get higher visibility in search results.</li>
              </ul>
            </GreyCard>
          </div>
          <div className="col-md-6">
            <GreyCard title="Reporting and Safety">
              <ul className="mb-0">
                <li>Report any suspicious users or spam messages via the Report button.</li>
                <li>Never share confidential business information (e.g., invoices, supplier details) outside the platform.</li>
              </ul>
            </GreyCard>
          </div>
          <div className="col-md-6">
            <GreyCard title="Communication">
              <ul className="mb-0">
                <li>Use professional language in all posts and messages.</li>
                <li>Avoid spam promotions, irrelevant links, or repetitive messaging.</li>
                <li>Respect others' time and privacy, don't send unnecessary messages.</li>
              </ul>
            </GreyCard>
          </div>
        </div>
        <p className="mb-5">
          By following these guidelines, you help maintain a secure and
          trustworthy business community on Selectezi.
        </p>

        {/* Accessing Guide */}
        <h2 className="sz-plain-title mb-1">8. Accessing This User Guide</h2>
        <p className="mb-3">
          You can read or download the official Selectezi User Guide anytime
          through these sections on the platform:
        </p>
        <div className="row g-3 mb-3">
          <div className="col-md-4">
            <GreyCard title="1. Website Footer">
              <p className="fw-semibold mb-1">Help Center</p>
              <p className="mb-0">Includes FAQs, tutorials, and the complete User Guide.</p>
            </GreyCard>
          </div>
          <div className="col-md-4">
            <GreyCard title="2. Dashboard">
              <p className="fw-semibold mb-1">Help / Support Tab</p>
              <p className="mb-0">Quick access while logged into your account.</p>
            </GreyCard>
          </div>
          <div className="col-md-4">
            <GreyCard title="3. Signup or Login Page">
              <p className="fw-semibold mb-1">Need Help? Link</p>
              <p className="mb-0">Guides new users during registration or email verification.</p>
            </GreyCard>
          </div>
        </div>
        <p className="mb-5">
          The User Guide is your go-to resource, from account creation to
          posting ads and solving problems.
        </p>

        {/* System Updates */}
        <h2 className="sz-plain-title mb-1">9. System Updates and Future Improvements</h2>
        <p className="mb-5">
          Selectezi is constantly evolving to provide a smoother, safer, and
          more advanced user experience. Our Product and Development Teams
          regularly release updates to improve performance, fix bugs, and
          introduce new features.
        </p>

        {/* Summary */}
        <h2 className="sz-plain-title mb-1">10. Summary: Complete User Journey</h2>
        <div className="sz-outer-card">
        <p className="mb-3">
          The Selectezi User Guide and Helpline is your all-in-one reference
          for understanding, navigating, and using the platform effectively.
        </p>
        <p className="fw-semibold mb-2">Here's what it helps you do:</p>
        <ul className="sz-bullet-list mb-4">
          <li>Create and Confirm Your Account, quickly and securely</li>
          <li>Learn Platform Features: Profile, Timeline, Ads, Messenger</li>
          <li>Report and Fix Problems, with a clear support process</li>
          <li>Follow Rules, ensuring a respectful business environment</li>
          <li>Stay Updated, with continuous improvements and pro tools</li>
        </ul>

        <InfoBox bold>
          <p className="mb-1">Our Mission</p>
          <p className="mb-0 fw-normal">
            To build a professional, trusted, and verified business network
            where businesses, retailers, and service providers grow together
            through transparency, technology, and meaningful connections.
          </p>
        </InfoBox>
        </div>
      </div>
    </div>
  );
}
